import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { WorkspacesService, type Workspace } from "@/api";
import { useAuth } from "@/hooks/use-auth";

interface ActiveWorkspaceContextValue {
  /** Workspaces visible to the caller (already tenant-scoped at the
   *  backend; non-admins only see ones they belong to). */
  workspaces: Workspace[];
  /** Active workspace derived from the JWT (``user.workspace_id``)
   *  cross-referenced with the cached list. ``null`` until both
   *  load. */
  activeWorkspace: Workspace | null;
  /** Switch the active workspace. Rotates the JWT pair via the
   *  existing ``/v1/auth/switch-workspace`` endpoint so the change
   *  is server-authoritative — any subsequent request with the
   *  rotated cookie carries the new workspace claim. Components
   *  re-fetch on ``user.workspace_id`` change. */
  setActiveWorkspaceId: (id: string) => Promise<void>;
  /** Re-pull the workspace list after a create / archive. Cheap
   *  call (single GET; no streaming). */
  refreshWorkspaces: () => Promise<void>;
  isLoading: boolean;
  /** Error from the most recent list fetch, surfaced so callers
   *  can render an inline ``Alert`` instead of failing silently. */
  error: Error | null;
}

const ActiveWorkspaceContext = createContext<ActiveWorkspaceContextValue | null>(
  null
);

/**
 * Active-workspace provider. Source of truth for "which workspace
 * the caller is operating in" is the JWT claim (``user.workspace_id``)
 * — switching means rotating tokens via ``useAuth.switchWorkspace``,
 * which the existing ``WorkspaceSelector`` already wires.
 *
 * This context layers two conveniences on top:
 *
 * 1. A cached workspace list (so the switcher dropdown and the
 *    Workspaces page don't refetch independently).
 * 2. A ``refreshWorkspaces`` hook callers invoke after a
 *    create / archive so the cached list catches up without
 *    waiting for a JWT round-trip.
 *
 * Deliberate non-feature: no localStorage. The JWT cookie already
 * persists across reload + carries the active workspace claim.
 * Adding a separate localStorage key would create two sources of
 * truth that can drift (FE-side localStorage vs server-side JWT
 * claim).
 */
export function ActiveWorkspaceProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { user, isAuthenticated } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const refreshWorkspaces = useCallback(async () => {
    if (!isAuthenticated) {
      setWorkspaces([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const items = await WorkspacesService.list();
      setWorkspaces(items);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Hydrate on auth + re-pull whenever the user identity changes
  // (login, logout, workspace switch).
  useEffect(() => {
    void refreshWorkspaces();
  }, [refreshWorkspaces, user?.user_id, user?.workspace_id]);

  const { switchWorkspace } = useAuth();
  const setActiveWorkspaceId = useCallback(
    async (id: string) => {
      if (id === user?.workspace_id) return;
      await switchWorkspace(id);
      // ``useAuth.switchWorkspace`` already refreshes the user
      // payload; the workspaces effect above re-runs on the new
      // ``user.workspace_id`` and picks the active workspace up.
    },
    [switchWorkspace, user?.workspace_id]
  );

  const activeWorkspace =
    workspaces.find((w) => w.id === user?.workspace_id) ?? null;

  return (
    <ActiveWorkspaceContext.Provider
      value={{
        workspaces,
        activeWorkspace,
        setActiveWorkspaceId,
        refreshWorkspaces,
        isLoading,
        error,
      }}
    >
      {children}
    </ActiveWorkspaceContext.Provider>
  );
}

export function useActiveWorkspace(): ActiveWorkspaceContextValue {
  const ctx = useContext(ActiveWorkspaceContext);
  if (!ctx) {
    throw new Error(
      "useActiveWorkspace must be used inside ActiveWorkspaceProvider."
    );
  }
  return ctx;
}
